import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, Menu, Search, UserCircle2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useApi, useAction, useDemo } from '../../context/DemoContext';
import { dtt, http } from '../../lib/api';
import { ROLE_LABEL, homeFor } from './nav';

function GlobalSearch() {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    if (q.length < 2) { setRes([]); return undefined; }
    const t = setTimeout(() => { http.get('/search', { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes([])); }, 220);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
      <input className="input pl-9" placeholder="Search SMEs, equipment, applications, cases…" value={q} data-testid="global-search-input"
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)} />
      {open && q.length >= 2 && (
        <div className="absolute z-[1200] mt-1 w-full overflow-hidden rounded-md border border-[#2B3B60] bg-[#0D1424] shadow-2xl" data-testid="search-results">
          {res.length === 0 && <div className="px-3 py-3 text-sm text-slate-500">No accessible records match “{q}”.</div>}
          {res.map((r) => (
            <button key={r.type + r.id} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[#162036]" data-testid={`search-result-${r.id}`}
              onMouseDown={() => { nav(r.link); setQ(''); }}>
              <span className="w-20 shrink-0 font-mono text-[10px] uppercase text-[#00A9CE]">{r.type}</span>
              <span className="min-w-0"><span className="block truncate text-sm text-white">{r.label}</span><span className="block truncate text-xs text-slate-500">{r.sub}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const { data } = useApi('/notifications');
  const act = useAction();
  const nav = useNavigate();
  const unread = (data || []).filter((n) => !n.read).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative rounded-md p-2 text-slate-300 hover:bg-[#162036]" data-testid="notifications-btn">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-[#FFCD00] px-1.5 font-mono text-[10px] font-bold text-[#0A0F1D]" data-testid="notifications-count">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[1200] w-96 border-[#2B3B60] bg-[#0D1424] p-0">
        <div className="flex items-center justify-between border-b border-[#1E2A45] px-4 py-2.5">
          <span className="font-display font-semibold">Notifications</span>
          <button className="text-xs text-[#00A9CE]" data-testid="notifications-read-all" onClick={() => act(() => http.post('/notifications/read-all'))}>Mark all read</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {(data || []).length === 0 && <div className="p-4 text-sm text-slate-500">No notifications for this role.</div>}
          {(data || []).map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} className="block w-full border-b border-[#1E2A45]/60 px-4 py-2.5 text-left hover:bg-[#162036]">
              <div className="flex items-center gap-2">{!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#FFCD00]" />}<span className="text-sm text-white">{n.title}</span></div>
              <div className="mt-0.5 text-xs text-slate-400">{n.body}</div>
              <div className="mt-0.5 font-mono text-[10px] text-slate-500">{dtt(n.ts)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RoleSwitcher() {
  const { me, users, switchUser } = useDemo();
  const nav = useNavigate();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 rounded-md border border-[#2B3B60] px-2.5 py-1.5 hover:border-[#00A9CE]/60" data-testid="role-switcher-btn">
          <UserCircle2 className="h-5 w-5 text-[#00A9CE]" />
          <span className="hidden text-left sm:block">
            <span className="block text-xs font-medium leading-tight text-white" data-testid="current-user-name">{me?.name || '…'}</span>
            <span className="block text-[10px] leading-tight text-[#FFCD00]" data-testid="current-role-label">{ROLE_LABEL[me?.role]}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[1200] w-80 border-[#2B3B60] bg-[#0D1424] p-2">
        <div className="eyebrow px-2 pb-2 pt-1">Presenter role switcher — demo personas</div>
        {users.map((u) => (
          <button key={u.id} data-testid={`switch-role-${u.id}`} onClick={() => { switchUser(u.id); nav(homeFor(u.role)); }}
            className={`mb-1 block w-full rounded-md px-3 py-2 text-left hover:bg-[#162036] ${me?.id === u.id ? 'bg-[#FFCD00]/10 ring-1 ring-[#FFCD00]/40' : ''}`}>
            <div className="flex items-center justify-between"><span className="text-sm text-white">{u.name}</span><span className="font-mono text-[10px] text-[#FFCD00]">{ROLE_LABEL[u.role]}</span></div>
            <div className="text-xs text-slate-500">{u.title} · {u.org_name}</div>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Topbar({ onMenu }) {
  const { me } = useDemo();
  return (
    <header className="sticky top-0 z-[1000] flex h-16 items-center gap-3 border-b border-[#1E2A45] bg-[#0D1424]/95 px-4 backdrop-blur-md lg:px-6" data-testid="topbar">
      <button className="lg:hidden text-slate-300" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
      {me?.role !== 'admin' ? <GlobalSearch /> : <div className="flex-1 text-sm text-slate-500">Platform operations — no default access to confidential records</div>}
      <div className="ml-auto flex items-center gap-2">
        <span className="hidden items-center gap-1.5 rounded border border-[#FFCD00]/40 bg-[#FFCD00]/10 px-2 py-1 font-mono text-[10px] font-bold tracking-wider text-[#FFCD00] md:flex" data-testid="demo-mode-indicator">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#FFCD00]" />DEMONSTRATION / SYNTHETIC DATA
        </span>
        <Notifications />
        <RoleSwitcher />
      </div>
    </header>
  );
}
